/**
 * Printer Helper — the single dialog for printer status, setup and
 * troubleshooting (replaces the old standalone PrinterTroubleshootDialog).
 *
 * Two views in one dialog:
 *   - `status`      — a clear connection banner; when connected it collapses to a
 *                     simple success state whose obvious action is Close. When not
 *                     connected it offers quick reconnect to remembered printers
 *                     and a Troubleshoot button.
 *   - `troubleshoot`— SDK checks + a one-tap reconnect, or (no saved printer) an
 *                     inline scan/connect list.
 *
 * Opened exclusively through the store via `PrinterHelperHost` (mounted once,
 * globally). All triggers call `usePrinterStore.openHelper(mode)`.
 */
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import MuiPrintIcon from '@mui/icons-material/Print';
import ListItemText from '@mui/material/ListItemText';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiArrowBackIcon from '@mui/icons-material/ArrowBack';
import MuiExpandLessIcon from '@mui/icons-material/ExpandLess';
import MuiExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useBoolean } from 'src/hooks/use-boolean';

import { usePrinterStore } from 'src/store/PrinterStore';
import { checkPrinterReadiness } from 'src/services/printing';
import type { PrinterHelperMode } from 'src/store/PrinterStore/types';
import { starPrintService, type SavedPrinter, type StarPrinterStatus } from 'src/services/printers';

import Label from 'src/components/label';
import MuiIcon from 'src/components/mui-icon';
import { useSnackbar } from 'src/components/snackbar';

import PrinterStatusCard from 'src/sections/settings/tabs/system/printers/printer-status-card';

import PrinterTroubleshootView from './printer-troubleshoot-view';
import PrinterStatusBanner, { derivePrinterStatus } from './printer-status-banner';

// ----------------------------------------------------------------------

interface PrinterReadinessDialogProps {
  open: boolean;
  onClose: () => void;
  initialView?: PrinterHelperMode;
  pendingRetry?: (() => Promise<boolean>) | null;
}

function isStatusHealthy(status: StarPrinterStatus | null): boolean {
  return Boolean(
    status && status.online && !status.coverOpen && !status.paperEmpty && !status.cutterError
  );
}

export default function PrinterReadinessDialog({
  open,
  onClose,
  initialView = 'status',
  pendingRetry = null,
}: PrinterReadinessDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const activePrinter = usePrinterStore((s) => s.activePrinter);
  const rememberedPrinters = usePrinterStore((s) => s.rememberedPrinters);
  const lastError = usePrinterStore((s) => s.lastError);

  const [view, setView] = useState<PrinterHelperMode>(initialView);
  const refreshing = useBoolean(false);
  const options = useBoolean(false);
  const [status, setStatus] = useState<StarPrinterStatus | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const connected = status?.connected ?? Boolean(starPrintService.getActiveConnectionId());
  const ready = connected && isStatusHealthy(status);
  // "Connecting" reflects an active printer switch — a plain status refresh keeps
  // the last-known banner rather than flickering to Connecting.
  const busy = switchingId !== null;

  // Follow the requested view when (re)opening or when an external trigger
  // changes the mode (e.g. a hardware-error auto-opens troubleshoot).
  useEffect(() => {
    if (open) setView(initialView);
  }, [open, initialView]);

  // Hydrate from the cached status and follow live updates while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    setStatus(starPrintService.getLastStatus());
    (async () => {
      unsubscribe = await starPrintService.addStatusListener((next) => {
        if (!cancelled) setStatus(next);
      });
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [open]);

  const handleRefresh = useCallback(async () => {
    refreshing.onTrue();
    try {
      setStatus(await starPrintService.getPrinterStatus());
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Could not read printer status', {
        variant: 'error',
      });
    } finally {
      refreshing.onFalse();
    }
  }, [refreshing, enqueueSnackbar]);

  /** Persist `printer` as active, then re-verify readiness. */
  const activatePrinter = useCallback(
    async (printer: SavedPrinter) => {
      setSwitchingId(printer.identifier);
      try {
        await usePrinterStore.getState().setActivePrinter(printer);
        await checkPrinterReadiness({ force: true });
        setStatus(starPrintService.getLastStatus());
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Could not switch printer', {
          variant: 'error',
        });
      } finally {
        setSwitchingId(null);
      }
    },
    [enqueueSnackbar]
  );

  const inTroubleshoot = view === 'troubleshoot';
  const statusInfo = derivePrinterStatus({
    hasPrinter: Boolean(activePrinter),
    connected,
    busy,
    ready,
    errorMessage: lastError?.message,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {inTroubleshoot ? (
            <IconButton onClick={() => setView('status')} edge="start" aria-label="Back">
              <MuiIcon icon={MuiArrowBackIcon} />
            </IconButton>
          ) : (
            <Avatar sx={{ bgcolor: 'warning.main' }}>
              <MuiIcon icon={MuiPrintIcon} />
            </Avatar>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {inTroubleshoot ? 'Troubleshoot Printer' : 'Printer'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {inTroubleshoot ? 'Reconnect or switch printers' : 'Connection status and setup'}
            </Typography>
          </Box>
          {/* Compact connection status, right-aligned (troubleshoot view only —
              status view shows the full banner below instead). */}
          {inTroubleshoot && (
            <Label color={statusInfo.color} variant="soft">
              {statusInfo.label}
            </Label>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Stack spacing={2.5} sx={{ p: 3, pt: 1 }}>
          {!inTroubleshoot && (
            <PrinterStatusBanner
              hasPrinter={Boolean(activePrinter)}
              connected={connected}
              busy={busy}
              ready={ready}
              errorMessage={lastError?.message}
            />
          )}

          {inTroubleshoot ? (
            <PrinterTroubleshootView
              status={status}
              pendingRetry={pendingRetry}
              onConnected={() => setView('status')}
            />
          ) : (
            <StatusView
              activePrinter={activePrinter}
              rememberedPrinters={rememberedPrinters}
              status={status}
              connected={connected}
              refreshing={refreshing.value}
              switchingId={switchingId}
              optionsOpen={connected ? options.value : true}
              onToggleOptions={options.onToggle}
              showOptionsToggle={connected}
              onRefresh={handleRefresh}
              onUse={activatePrinter}
              onTroubleshoot={() => setView('troubleshoot')}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant={connected && !inTroubleshoot ? 'contained' : 'outlined'}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ----------------------------------------------------------------------

interface StatusViewProps {
  activePrinter: SavedPrinter | null;
  rememberedPrinters: SavedPrinter[];
  status: StarPrinterStatus | null;
  connected: boolean;
  refreshing: boolean;
  switchingId: string | null;
  optionsOpen: boolean;
  showOptionsToggle: boolean;
  onToggleOptions: () => void;
  onRefresh: () => void;
  onUse: (printer: SavedPrinter) => void;
  onTroubleshoot: () => void;
}

/** The default status/setup view. When connected, setup collapses behind a toggle. */
function StatusView({
  activePrinter,
  rememberedPrinters,
  status,
  connected,
  refreshing,
  switchingId,
  optionsOpen,
  showOptionsToggle,
  onToggleOptions,
  onRefresh,
  onUse,
  onTroubleshoot,
}: StatusViewProps) {
  const others = rememberedPrinters.filter((p) => p.identifier !== activePrinter?.identifier);

  return (
    <>
      {/* Active printer summary */}
      {activePrinter && (
        <Card sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <MuiIcon icon={MuiPrintIcon} width={28} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2">
                {activePrinter.name ?? activePrinter.model}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {activePrinter.model} · {activePrinter.interfaceType.toUpperCase()}
              </Typography>
            </Box>
            {connected && (
              <Label color="success" variant="soft">
                Connected
              </Label>
            )}
          </Box>
        </Card>
      )}

      {/* When connected, hide setup behind a toggle so Close is the obvious action. */}
      {showOptionsToggle && (
        <Button
          color="inherit"
          onClick={onToggleOptions}
          endIcon={<MuiIcon icon={optionsOpen ? MuiExpandLessIcon : MuiExpandMoreIcon} />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Printer options
        </Button>
      )}

      <Collapse in={optionsOpen} unmountOnExit>
        <Stack spacing={2.5}>
          <PrinterStatusCard
            status={status}
            connected={connected}
            refreshing={refreshing}
            onRefresh={onRefresh}
          />

          {others.length > 0 && (
            <Card sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Remembered Printers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Reconnect to a printer you&apos;ve used before.
              </Typography>
              <List sx={{ p: 0 }}>
                {others.map((printer) => (
                  <ListItem
                    key={printer.identifier}
                    sx={{ px: 0 }}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={switchingId !== null}
                        onClick={() => onUse(printer)}
                      >
                        {switchingId === printer.identifier ? 'Connecting…' : 'Use'}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={printer.name ?? printer.model}
                      secondary={`${printer.model} · ${printer.interfaceType.toUpperCase()}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<MuiIcon icon={MuiSettingsIcon} />}
            onClick={onTroubleshoot}
          >
            {activePrinter ? 'Troubleshoot' : 'Find a printer'}
          </Button>
        </Stack>
      </Collapse>
    </>
  );
}

// ----------------------------------------------------------------------

/**
 * The single, global mount for the Printer Helper. Reads open-state from the
 * store so every trigger (`openHelper`/`openTroubleshoot`) drives one instance.
 */
export function PrinterHelperHost() {
  const helperOpen = usePrinterStore((s) => s.helperOpen);
  const helperMode = usePrinterStore((s) => s.helperMode);
  const pendingRetry = usePrinterStore((s) => s.pendingRetry);
  const closeHelper = usePrinterStore((s) => s.closeHelper);

  return (
    <PrinterReadinessDialog
      open={helperOpen}
      onClose={closeHelper}
      initialView={helperMode}
      pendingRetry={pendingRetry}
    />
  );
}
